import { createEntityAdapter, Dictionary, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { RemoveUserAction, UserCreatedAction, UsersAdapterActions, UsersLoadedAction, UserUpdatedAction } from '../actions';
import { User } from '../../models/domain';

export interface UsersState extends EntityState<User> {
  isLoaded: boolean;
  isLoading: boolean;
  pendingRemovals: Dictionary<User>
}

function selectUserId(user: User): any {
  return user.email;
}

export function sortByName(user1: User, user2: User): number {
  return `${user1.first_name} ${user1.last_name}`.localeCompare(`${user2.first_name} ${user2.last_name}`);
}

export const usersAdapter: EntityAdapter<User> = createEntityAdapter<User>({
  selectId: selectUserId,
  sortComparer: sortByName
});

const initialState: UsersState = usersAdapter.getInitialState({ isLoaded: false, pendingRemovals: {}, isLoading: false });

const adapterReducer = createReducer(
  initialState,
  on(UsersAdapterActions.loadUsers, (state) => {
    return {
      ...state,
      isLoading: true
    };
  }),
  on(UsersAdapterActions.usersLoaded, (state: UsersState, action: UsersLoadedAction) =>
    usersAdapter.upsertMany(action.payload, { ...state, isLoaded: true, isLoading: false })
  ),
  on(UsersAdapterActions.userUpdated, (state: UsersState, action: UserUpdatedAction) => {
    return usersAdapter.upsertOne(action.user, state);
  }),
  on(UsersAdapterActions.userCreated, (state: UsersState, action: UserCreatedAction) => {
    return usersAdapter.upsertOne(action.payload, state);
  }),
  on(UsersAdapterActions.removeUser, (state, action) => {
    return {
      ...usersAdapter.removeOne(action.payload.email, state),
      pendingRemovals: {
        ...state.pendingRemovals,
        [action.payload.email]: state.entities[action.payload.email],
      },
    };
  }),
  on(UsersAdapterActions.userRemoved, (state: UsersState, action: RemoveUserAction) => usersAdapter.removeOne(action.payload.email, state)),
  on(UsersAdapterActions.userRemovedSuccessfully, (state: UsersState, action) => {
    const pendingRemovals = { ...state.pendingRemovals };
    delete pendingRemovals[action.email];

    return {
      ...state,
      pendingRemovals,
    };
  }),
  on(UsersAdapterActions.userRemovalFailed, (state: UsersState, action) => {
    const toReturnBack = state.pendingRemovals[action.email];
    const pendingRemovals = { ...state.pendingRemovals };
    delete pendingRemovals[action.email];

    return {
      ...usersAdapter.addOne(toReturnBack, state),
      pendingRemovals
    };
  }),
);

export function usersReducer(state = initialState, action: Action): UsersState {
  return adapterReducer(state, action);
}
